/**
 * ua-helper.js version 4 (Final Production Version)
 * Optimized for SweldoNow - Bacoor, Cavite
 * Goals: Fix "Apple" on Windows bug, Stable Fingerprint, Unified Summary, Correct iOS Detection.
 */

export async function getUAData() {
    const ua = navigator.userAgent || "";
    const uaData = navigator.userAgentData;

    let browserName = "Unknown";
    let deviceModel = "";
    let deviceType = "Desktop";
    let osName = "Unknown";
    let osVersion = "";
    let vendor = "Unknown";

    // 1. Get Modern Client Hints (High Entropy)
    if (uaData) {
        try {
            const highEntropy = await uaData.getHighEntropyValues([
                "platform",
                "platformVersion",
                "model"
            ]);

            osName = highEntropy.platform || "Unknown";
            osVersion = highEntropy.platformVersion || "";
            deviceModel = highEntropy.model || "";
            deviceType = uaData.mobile ? "Mobile" : "Desktop";
        } catch (e) {
            console.warn("UA Hints failed, falling back to regex.");
        }
    }

    // 2. Legacy OS & Type Fallback (If Hints failed or OS is Unknown/Misidentified)
    // Fix for iOS/iPadOS identifying as MacOS in Safari/PWA
    if (osName === "Unknown" || osName === "" || osName === "MacOS") {
        const isIOS = /iPhone|iPad|iPod/i.test(ua) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (isIOS) {
            osName = "iOS";
            deviceType = /iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) 
                         ? "Tablet" : "Mobile";
        } else if (/Windows/i.test(ua)) {
            osName = "Windows";
        } else if (/Android/i.test(ua)) {
            osName = "Android";
        } else if (/Linux/i.test(ua)) {
            osName = "Linux";
        }

        if (deviceType === "Desktop") {
            if (/ipad|tablet/i.test(ua)) deviceType = "Tablet";
            else if (/mobile/i.test(ua)) deviceType = "Mobile";
        }
    }

    // 3. Browser Detection (Pass OS to prevent "Apple/Safari" grease on Windows)
    if (uaData && uaData.brands) {
        browserName = normalizeBrowserName(uaData.brands, osName);
    } else {
        const browserMatch = ua.match(/(Edg|Chrome|Firefox|Safari)/i);
        browserName = browserMatch ? browserMatch[0].replace("Edg", "Edge") : "Unknown";
    }

    // 4. Vendor & Friendly Name Detection
    vendor = detectVendor(deviceModel, ua, osName);
    const deviceDisplayName = generateFriendlyName({
        deviceModel,
        deviceType,
        osName,
        vendor
    });

    // 5. Stable Fingerprint (Hardware + OS + Locale)
    const fingerprintSource = [
        osName.toLowerCase(),
        deviceType.toLowerCase(),
        (deviceModel || "generic").toLowerCase(),
        (navigator.hardwareConcurrency || 4).toString(),        
        (navigator.platform || "unknown").toLowerCase(),    
    ].join("|");

    const fingerprint = await sha256(fingerprintSource);

    // 6. Final Formatting for SweldoNow UI
    let finalName = "";
    if (deviceType === 'Mobile' || deviceType === 'Tablet') {
        // Format: "Samsung Android SM-A528B"
        const vendorPrefix = (vendor !== "Unknown" && vendor !== "Apple") ? `${vendor} ` : "";
        finalName = `${vendorPrefix}${osName} ${deviceModel || 'Phone'}`.trim();
        
        // Specific cleanup for Apple products
        if (osName === "iOS") finalName = deviceModel && deviceModel !== "Unknown" ? deviceModel : "iPhone";
    } else {
        // Desktop: Returns "Windows PC", "Mac Computer", etc.
        finalName = deviceDisplayName;
    }
    const deviceName = finalName;

    return {
        browser: { name: browserName },
        device: {
            name: deviceDisplayName,
            model: deviceModel || "Unknown",
            type: deviceType,
            vendor: vendor
        },
        os: { name: osName, version: osVersion },
        fingerprint,
        deviceName 
    };
}

/**
 * Filters out fake "Apple" or "Safari" brands injected by Chrome on Windows.
 */
function normalizeBrowserName(brands, osName) {
    const grease = ["Not", "Brand", "Chromium"];
    if (osName !== "MacOS" && osName !== "iOS") {
        grease.push("Apple", "Safari");
    }
    const priority = ["Edge", "Google Chrome", "Chrome", "Brave", "Firefox"];
    const found = brands.find(b => 
        priority.some(p => b.brand.includes(p)) && 
        !grease.some(g => b.brand.includes(g))
    );
    return found ? found.brand.replace("Google ", "") : "Chrome";
}

/**
 * Detects Dell, HP, Lenovo, etc., focusing on Windows sub-brands.
 */
function detectVendor(model, ua, osName) {
    const input = (model + " " + ua).toLowerCase();
    if (osName === "Windows") {
        if (/dell|vostro|inspiron|latitude|precision/.test(input)) return "Dell";
        if (/lenovo|thinkpad|ideapad/.test(input)) return "Lenovo";
        if (/hp|hewlett|pavilion|elitebook/.test(input)) return "HP";
        if (/asus|rog|zenbook/.test(input)) return "Asus";
        if (/acer|aspire/.test(input)) return "Acer";
        return "Unknown"; 
    }
    if (osName === "MacOS" || osName === "iOS") return "Apple";
    if (/samsung|sm-/.test(input)) return "Samsung";
    if (/pixel|google/.test(input)) return "Google";
    if (/huawei/.test(input)) return "Huawei";
    return "Unknown";
}

/**
 * Generates the user-friendly string (e.g., "Dell Windows PC").
 */
function generateFriendlyName({ deviceModel, deviceType, osName, vendor }) {
    if (deviceModel && !["Unknown", "generic", ""].includes(deviceModel)) {
        return deviceModel;
    }
    const vendorPrefix = vendor !== "Unknown" ? `${vendor} ` : "";
    if (osName === "Windows") return `${vendorPrefix}Windows PC`.trim();
    if (osName === "MacOS") return "Mac Computer";
    if (osName === "iOS") return "iPhone";
    if (osName === "Linux") return `${vendorPrefix}Linux PC`.trim();
    if (deviceType === "Mobile") return `${vendorPrefix}${osName} Phone`.trim();
    if (deviceType === "Tablet") return `${vendorPrefix}${osName} Tablet`.trim();
    return `${vendorPrefix}${osName} Device`.trim();
}

/**
 * Standard SHA-256 for the unique device fingerprint.
 */
async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");
}
